/**
 * mapClientSearch: the one match both maps' client search runs.
 *
 * The candidates are the rows of the map's own client read (GET
 * /map/clients), already narrowed by the caller to what the map draws or
 * lists; nothing here reads anything else. A row matches when its display
 * name contains the typed text, ignoring case and the text's surrounding
 * spaces. Placed matches come first, then not-placed ones, each in the
 * order the read gave them (the server sorts by name). At most `limit`
 * rows are returned, and `more` counts the matches left over. An empty or
 * blank query matches nothing.
 */

import type { ClientMapNotPlacedWire, ClientMapPinWire } from '@/lib/capacityApi';

/** How many results the list shows while typing. */
export const MAP_CLIENT_SEARCH_LIMIT = 8;

export interface MapClientMatches {
  placed: ClientMapPinWire[];
  notPlaced: ClientMapNotPlacedWire[];
  /** Matches beyond `limit`, not shown. */
  more: number;
}

export function matchMapClients(
  pins: readonly ClientMapPinWire[],
  notPlaced: readonly ClientMapNotPlacedWire[],
  query: string,
  limit: number,
): MapClientMatches {
  const q = query.trim().toLowerCase();
  if (q === '') return { placed: [], notPlaced: [], more: 0 };
  const placedAll = pins.filter((p) => p.display_name.toLowerCase().includes(q));
  const notPlacedAll = notPlaced.filter((c) => c.display_name.toLowerCase().includes(q));
  const placed = placedAll.slice(0, limit);
  const notPlacedShown = notPlacedAll.slice(0, Math.max(0, limit - placed.length));
  return {
    placed,
    notPlaced: notPlacedShown,
    more: placedAll.length + notPlacedAll.length - placed.length - notPlacedShown.length,
  };
}
